import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import kodyMascot from "@/assets/kody-mascot.webp";
import {
  Rocket,
  Shield,
  Globe,
  Layers,
  Users,
  Award,
  Code2,
  ExternalLink,
  Sparkles,
  Crown,
  Bot,
} from "lucide-react";

const content: Record<string, {
  badge: string;
  title: string;
  subtitle: string;
  intro: string;
  stats: { value: string; label: string }[];
  pillars: { icon: React.ElementType; title: string; desc: string }[];
  team: string;
  cta: string;
}> = {
  es: {
    badge: "#1 Crypto Wallet del Mundo",
    title: "Solo fue posible gracias a CodeMasterIP",
    subtitle: "La creación de Kody y su equipo de profesionales ha hecho realidad el wallet crypto más completo y profesional jamás construido.",
    intro: "CryptoKody no es solo una app más. Es el resultado de miles de horas de ingeniería, diseño y visión de producto lideradas por CodeMasterIP — la mente detrás de Kody, la mascota IA que guía cada interacción. Con más de 200 herramientas integradas, soporte para 11 idiomas y 10+ blockchains, hemos redefinido lo que significa un portfolio tracker.",
    stats: [
      { value: "200+", label: "Herramientas" },
      { value: "11", label: "Idiomas" },
      { value: "10+", label: "Blockchains" },
      { value: "24/7", label: "Monitoreo" },
    ],
    pillars: [
      { icon: Rocket, title: "Visión Ambiciosa", desc: "CodeMasterIP imaginó un ecosistema crypto unificado donde cualquier usuario pudiera gestionar todo desde un solo lugar." },
      { icon: Bot, title: "Kody: IA Nativa", desc: "Kody no es solo una mascota — es un asistente inteligente integrado en cada rincón de la plataforma." },
      { icon: Shield, title: "Seguridad Inquebrantable", desc: "Arquitectura read-only, 2FA, alertas de phishing y auditorías constantes. Tus claves nunca se almacenan." },
      { icon: Globe, title: "Alcance Global", desc: "11 idiomas con soporte RTL, conversión de divisas y datos localizados para usuarios de todo el mundo." },
      { icon: Layers, title: "200+ Herramientas Pro", desc: "Desde DCA simulators hasta MEV protection, yield farming, NFT galleries y análisis on-chain avanzado." },
      { icon: Users, title: "Equipo de Élite", desc: "Ingenieros, diseñadores y analistas crypto trabajando juntos bajo la dirección de CodeMasterIP." },
    ],
    team: "Detrás de cada línea de código, cada animación y cada widget hay un equipo apasionado que cree en hacer la tecnología crypto accesible para todos. CodeMasterIP lidera esta misión con Kody como su creación más ambiciosa.",
    cta: "Visitar CodeMasterIP",
  },
  en: {
    badge: "#1 Crypto Wallet in the World",
    title: "Only possible thanks to CodeMasterIP",
    subtitle: "The creation of Kody and his team of professionals made the most complete and professional crypto wallet ever built a reality.",
    intro: "CryptoKody is not just another app. It's the result of thousands of hours of engineering, design and product vision led by CodeMasterIP — the mind behind Kody, the AI mascot that guides every interaction. With 200+ integrated tools, support for 11 languages and 10+ blockchains, we've redefined what a portfolio tracker means.",
    stats: [
      { value: "200+", label: "Tools" },
      { value: "11", label: "Languages" },
      { value: "10+", label: "Blockchains" },
      { value: "24/7", label: "Monitoring" },
    ],
    pillars: [
      { icon: Rocket, title: "Ambitious Vision", desc: "CodeMasterIP envisioned a unified crypto ecosystem where any user could manage everything from one place." },
      { icon: Bot, title: "Kody: Native AI", desc: "Kody isn't just a mascot — it's an intelligent assistant integrated into every corner of the platform." },
      { icon: Shield, title: "Unbreakable Security", desc: "Read-only architecture, 2FA, phishing alerts and constant audits. Your keys are never stored." },
      { icon: Globe, title: "Global Reach", desc: "11 languages with RTL support, currency conversion and localized data for users worldwide." },
      { icon: Layers, title: "200+ Pro Tools", desc: "From DCA simulators to MEV protection, yield farming, NFT galleries and advanced on-chain analysis." },
      { icon: Users, title: "Elite Team", desc: "Engineers, designers and crypto analysts working together under CodeMasterIP's direction." },
    ],
    team: "Behind every line of code, every animation and every widget is a passionate team that believes in making crypto technology accessible to everyone. CodeMasterIP leads this mission with Kody as his most ambitious creation.",
    cta: "Visit CodeMasterIP",
  },
  zh: {
    badge: "全球 #1 加密钱包",
    title: "这一切只有感谢 CodeMasterIP 才得以实现",
    subtitle: "Kody 的创造及其专业团队打造了有史以来最完整、最专业的加密钱包。",
    intro: "CryptoKody 不仅仅是另一个应用。它是 CodeMasterIP 领导的数千小时工程、设计和产品愿景的成果。集成 200 多种工具，支持 11 种语言和 10 多条区块链。",
    stats: [{ value: "200+", label: "工具" }, { value: "11", label: "语言" }, { value: "10+", label: "区块链" }, { value: "24/7", label: "监控" }],
    pillars: [
      { icon: Rocket, title: "宏大愿景", desc: "CodeMasterIP 构想了一个统一的加密生态系统。" },
      { icon: Bot, title: "Kody: 原生AI", desc: "Kody 是集成在平台各处的智能助手。" },
      { icon: Shield, title: "坚不可摧的安全", desc: "只读架构、2FA、钓鱼警报。" },
      { icon: Globe, title: "全球覆盖", desc: "11种语言，RTL支持，本地化数据。" },
      { icon: Layers, title: "200+专业工具", desc: "从DCA模拟器到MEV保护。" },
      { icon: Users, title: "精英团队", desc: "工程师、设计师和加密分析师。" },
    ],
    team: "在每一行代码和每一个组件背后，都有一支充满激情的团队。CodeMasterIP 带领这一使命，Kody 是他最雄心勃勃的创作。",
    cta: "访问 CodeMasterIP",
  },
  hi: {
    badge: "दुनिया का #1 क्रिप्टो वॉलेट",
    title: "यह सब CodeMasterIP की बदौलत संभव हुआ",
    subtitle: "Kody और उनकी पेशेवर टीम ने अब तक का सबसे पूर्ण क्रिप्टो वॉलेट बनाया।",
    intro: "CryptoKody सिर्फ एक ऐप नहीं है। यह CodeMasterIP के नेतृत्व में हजारों घंटों की इंजीनियरिंग का परिणाम है।",
    stats: [{ value: "200+", label: "टूल्स" }, { value: "11", label: "भाषाएं" }, { value: "10+", label: "ब्लॉकचेन" }, { value: "24/7", label: "मॉनिटरिंग" }],
    pillars: [
      { icon: Rocket, title: "महत्वाकांक्षी दृष्टि", desc: "CodeMasterIP ने एक एकीकृत क्रिप्टो इकोसिस्टम की कल्पना की।" },
      { icon: Bot, title: "Kody: नेटिव AI", desc: "Kody प्लेटफॉर्म का बुद्धिमान सहायक है।" },
      { icon: Shield, title: "अटूट सुरक्षा", desc: "रीड-ओनली आर्किटेक्चर, 2FA।" },
      { icon: Globe, title: "वैश्विक पहुंच", desc: "11 भाषाएं, RTL सपोर्ट।" },
      { icon: Layers, title: "200+ प्रो टूल्स", desc: "DCA सिमुलेटर से MEV प्रोटेक्शन तक।" },
      { icon: Users, title: "एलीट टीम", desc: "इंजीनियर, डिजाइनर और क्रिप्टो विश्लेषक।" },
    ],
    team: "हर कोड लाइन के पीछे एक जुनूनी टीम है। CodeMasterIP इस मिशन का नेतृत्व करते हैं।",
    cta: "CodeMasterIP देखें",
  },
  ar: {
    badge: "المحفظة الكريبتو رقم 1 في العالم",
    title: "كل هذا أصبح ممكناً بفضل CodeMasterIP",
    subtitle: "أنشأ Kody وفريقه المحترف أكثر محفظة كريبتو اكتمالاً واحترافية على الإطلاق.",
    intro: "CryptoKody ليس مجرد تطبيق آخر. إنه نتيجة آلاف الساعات من الهندسة والتصميم بقيادة CodeMasterIP.",
    stats: [{ value: "+200", label: "أداة" }, { value: "11", label: "لغة" }, { value: "+10", label: "بلوكتشين" }, { value: "24/7", label: "مراقبة" }],
    pillars: [
      { icon: Rocket, title: "رؤية طموحة", desc: "تخيل CodeMasterIP نظام كريبتو موحد." },
      { icon: Bot, title: "Kody: ذكاء اصطناعي", desc: "Kody مساعد ذكي مدمج في كل مكان." },
      { icon: Shield, title: "أمان لا يُقهر", desc: "معمارية للقراءة فقط، مصادقة ثنائية." },
      { icon: Globe, title: "انتشار عالمي", desc: "11 لغة مع دعم RTL." },
      { icon: Layers, title: "+200 أداة احترافية", desc: "من محاكيات DCA إلى حماية MEV." },
      { icon: Users, title: "فريق نخبة", desc: "مهندسون ومصممون ومحللون." },
    ],
    team: "خلف كل سطر كود فريق شغوف. يقود CodeMasterIP هذه المهمة مع Kody كأعظم إبداعاته.",
    cta: "زيارة CodeMasterIP",
  },
  pt: {
    badge: "#1 Carteira Crypto do Mundo",
    title: "Só foi possível graças ao CodeMasterIP",
    subtitle: "A criação de Kody e sua equipe de profissionais tornou realidade a carteira crypto mais completa e profissional já construída.",
    intro: "CryptoKody não é apenas mais um app. É o resultado de milhares de horas de engenharia, design e visão de produto lideradas por CodeMasterIP.",
    stats: [{ value: "200+", label: "Ferramentas" }, { value: "11", label: "Idiomas" }, { value: "10+", label: "Blockchains" }, { value: "24/7", label: "Monitoramento" }],
    pillars: [
      { icon: Rocket, title: "Visão Ambiciosa", desc: "CodeMasterIP imaginou um ecossistema crypto unificado." },
      { icon: Bot, title: "Kody: IA Nativa", desc: "Kody é um assistente inteligente integrado." },
      { icon: Shield, title: "Segurança Inquebrantável", desc: "Arquitetura somente leitura, 2FA." },
      { icon: Globe, title: "Alcance Global", desc: "11 idiomas com suporte RTL." },
      { icon: Layers, title: "200+ Ferramentas Pro", desc: "De simuladores DCA a proteção MEV." },
      { icon: Users, title: "Equipe de Elite", desc: "Engenheiros, designers e analistas crypto." },
    ],
    team: "Por trás de cada linha de código há uma equipe apaixonada. CodeMasterIP lidera essa missão com Kody como sua criação mais ambiciosa.",
    cta: "Visitar CodeMasterIP",
  },
  fr: {
    badge: "#1 Wallet Crypto au Monde",
    title: "Tout cela n'a été possible que grâce à CodeMasterIP",
    subtitle: "La création de Kody et son équipe de professionnels a concrétisé le wallet crypto le plus complet et professionnel jamais construit.",
    intro: "CryptoKody n'est pas juste une app de plus. C'est le fruit de milliers d'heures d'ingénierie, de design et de vision produit menées par CodeMasterIP.",
    stats: [{ value: "200+", label: "Outils" }, { value: "11", label: "Langues" }, { value: "10+", label: "Blockchains" }, { value: "24/7", label: "Surveillance" }],
    pillars: [
      { icon: Rocket, title: "Vision Ambitieuse", desc: "CodeMasterIP a imaginé un écosystème crypto unifié." },
      { icon: Bot, title: "Kody: IA Native", desc: "Kody est un assistant intelligent intégré partout." },
      { icon: Shield, title: "Sécurité Inébranlable", desc: "Architecture en lecture seule, 2FA." },
      { icon: Globe, title: "Portée Mondiale", desc: "11 langues avec support RTL." },
      { icon: Layers, title: "200+ Outils Pro", desc: "Des simulateurs DCA à la protection MEV." },
      { icon: Users, title: "Équipe d'Élite", desc: "Ingénieurs, designers et analystes crypto." },
    ],
    team: "Derrière chaque ligne de code se trouve une équipe passionnée. CodeMasterIP mène cette mission avec Kody comme sa création la plus ambitieuse.",
    cta: "Visiter CodeMasterIP",
  },
  ru: {
    badge: "#1 крипто-кошелёк в мире",
    title: "Всё это стало возможным благодаря CodeMasterIP",
    subtitle: "Создание Kody и его команды профессионалов воплотило в жизнь самый полный и профессиональный крипто-кошелёк.",
    intro: "CryptoKody — это не просто приложение. Это результат тысяч часов инженерии, дизайна и продуктового видения под руководством CodeMasterIP.",
    stats: [{ value: "200+", label: "Инструментов" }, { value: "11", label: "Языков" }, { value: "10+", label: "Блокчейнов" }, { value: "24/7", label: "Мониторинг" }],
    pillars: [
      { icon: Rocket, title: "Амбициозное видение", desc: "CodeMasterIP представил единую криптоэкосистему." },
      { icon: Bot, title: "Kody: нативный ИИ", desc: "Kody — интеллектуальный помощник." },
      { icon: Shield, title: "Несокрушимая безопасность", desc: "Архитектура только для чтения, 2FA." },
      { icon: Globe, title: "Глобальный охват", desc: "11 языков с поддержкой RTL." },
      { icon: Layers, title: "200+ Про-инструментов", desc: "От DCA до защиты от MEV." },
      { icon: Users, title: "Элитная команда", desc: "Инженеры, дизайнеры и крипто-аналитики." },
    ],
    team: "За каждой строкой кода стоит страстная команда. CodeMasterIP ведёт эту миссию, а Kody — его самое амбициозное творение.",
    cta: "Посетить CodeMasterIP",
  },
  ja: {
    badge: "世界 #1 クリプトウォレット",
    title: "これはCodeMasterIPのおかげで実現しました",
    subtitle: "Kodyとそのプロフェッショナルチームが、史上最も完全で専門的なクリプトウォレットを実現しました。",
    intro: "CryptoKodyは単なるアプリではありません。CodeMasterIPが主導した数千時間のエンジニアリングの成果です。",
    stats: [{ value: "200+", label: "ツール" }, { value: "11", label: "言語" }, { value: "10+", label: "ブロックチェーン" }, { value: "24/7", label: "監視" }],
    pillars: [
      { icon: Rocket, title: "野心的ビジョン", desc: "CodeMasterIPは統一されたクリプトエコシステムを構想しました。" },
      { icon: Bot, title: "Kody: ネイティブAI", desc: "Kodyはプラットフォーム全体に統合されたインテリジェントアシスタント。" },
      { icon: Shield, title: "堅固なセキュリティ", desc: "読み取り専用アーキテクチャ、2FA。" },
      { icon: Globe, title: "グローバルリーチ", desc: "11言語、RTLサポート。" },
      { icon: Layers, title: "200+プロツール", desc: "DCAシミュレーターからMEV保護まで。" },
      { icon: Users, title: "エリートチーム", desc: "エンジニア、デザイナー、クリプトアナリスト。" },
    ],
    team: "すべてのコード行の背後には情熱的なチームがいます。CodeMasterIPがKodyを最も野心的な創作としてこのミッションをリードしています。",
    cta: "CodeMasterIPを訪問",
  },
  de: {
    badge: "#1 Krypto-Wallet der Welt",
    title: "Dies war nur dank CodeMasterIP möglich",
    subtitle: "Die Erschaffung von Kody und sein Team von Profis hat das vollständigste und professionellste Krypto-Wallet aller Zeiten realisiert.",
    intro: "CryptoKody ist nicht einfach eine weitere App. Es ist das Ergebnis tausender Stunden Engineering, Design und Produktvision unter der Leitung von CodeMasterIP.",
    stats: [{ value: "200+", label: "Tools" }, { value: "11", label: "Sprachen" }, { value: "10+", label: "Blockchains" }, { value: "24/7", label: "Überwachung" }],
    pillars: [
      { icon: Rocket, title: "Ambitionierte Vision", desc: "CodeMasterIP hat ein vereinheitlichtes Krypto-Ökosystem konzipiert." },
      { icon: Bot, title: "Kody: Native KI", desc: "Kody ist ein intelligenter Assistent in jedem Bereich." },
      { icon: Shield, title: "Unerschütterliche Sicherheit", desc: "Read-Only-Architektur, 2FA." },
      { icon: Globe, title: "Globale Reichweite", desc: "11 Sprachen mit RTL-Unterstützung." },
      { icon: Layers, title: "200+ Pro-Tools", desc: "Von DCA-Simulatoren bis MEV-Schutz." },
      { icon: Users, title: "Elite-Team", desc: "Ingenieure, Designer und Krypto-Analysten." },
    ],
    team: "Hinter jeder Codezeile steht ein leidenschaftliches Team. CodeMasterIP führt diese Mission mit Kody als seiner ehrgeizigsten Schöpfung.",
    cta: "CodeMasterIP besuchen",
  },
  ko: {
    badge: "세계 #1 크립토 지갑",
    title: "이 모든 것은 CodeMasterIP 덕분에 가능했습니다",
    subtitle: "Kody와 그의 전문가 팀이 역대 가장 완벽하고 전문적인 크립토 지갑을 실현했습니다.",
    intro: "CryptoKody는 단순한 앱이 아닙니다. CodeMasterIP가 이끈 수천 시간의 엔지니어링 결과물입니다.",
    stats: [{ value: "200+", label: "도구" }, { value: "11", label: "언어" }, { value: "10+", label: "블록체인" }, { value: "24/7", label: "모니터링" }],
    pillars: [
      { icon: Rocket, title: "야심찬 비전", desc: "CodeMasterIP는 통합된 크립토 생태계를 구상했습니다." },
      { icon: Bot, title: "Kody: 네이티브 AI", desc: "Kody는 플랫폼 전체에 통합된 지능형 어시스턴트입니다." },
      { icon: Shield, title: "견고한 보안", desc: "읽기 전용 아키텍처, 2FA." },
      { icon: Globe, title: "글로벌 도달", desc: "11개 언어, RTL 지원." },
      { icon: Layers, title: "200+ 프로 도구", desc: "DCA 시뮬레이터부터 MEV 보호까지." },
      { icon: Users, title: "엘리트 팀", desc: "엔지니어, 디자이너, 크립토 분석가." },
    ],
    team: "모든 코드 라인 뒤에는 열정적인 팀이 있습니다. CodeMasterIP는 Kody를 가장 야심찬 창작물로 이 미션을 이끌고 있습니다.",
    cta: "CodeMasterIP 방문",
  },
};

const CodeMasterShowcase = () => {
  const { lang } = useLanguage();
  const c = content[lang] || content.en;

  return (
    <section className="relative overflow-hidden border-t border-border/30 bg-gradient-to-b from-card/50 via-background to-card/30">
      {/* Ambient effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-primary/3 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-16 sm:py-24 relative z-10">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm">
            <Crown className="w-4 h-4 text-primary" />
            <span className="text-xs sm:text-sm font-bold text-primary tracking-wide">{c.badge}</span>
            <Sparkles className="w-4 h-4 text-primary animate-pulse" />
          </div>
        </motion.div>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <div className="flex justify-center mb-6">
            <motion.img
              src={kodyMascot}
              alt="Kody mascot"
              width={112}
              height={112}
              loading="lazy"
              className="w-20 h-20 sm:w-28 sm:h-28 object-contain drop-shadow-[0_0_30px_hsl(168_80%_48%/0.3)]"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-foreground leading-tight mb-4">
            {c.title}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            {c.subtitle}
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-3xl mx-auto mb-16"
        >
          {c.stats.map((stat, i) => (
            <div
              key={i}
              className="text-center p-4 sm:p-6 rounded-2xl bg-card/60 border border-border/40 backdrop-blur-sm"
            >
              <div className="text-2xl sm:text-4xl font-black text-primary mb-1">{stat.value}</div>
              <div className="text-xs sm:text-sm text-muted-foreground font-medium">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Intro paragraph */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl mx-auto text-center mb-16"
        >
          {c.intro}
        </motion.p>

        {/* Pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto mb-16">
          {c.pillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * i }}
                className="group p-5 sm:p-6 rounded-2xl bg-card/60 border border-border/40 backdrop-blur-sm hover:border-primary/30 hover:bg-card/80 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-foreground mb-2">{pillar.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{pillar.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Team quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto mb-12"
        >
          <div className="relative p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-primary/5 via-card/60 to-primary/5 border border-primary/20 backdrop-blur-sm">
            <Code2 className="absolute top-4 left-4 w-6 h-6 text-primary/20" />
            <p className="text-sm sm:text-base text-foreground/80 leading-relaxed italic text-center px-6">
              "{c.team}"
            </p>
            <div className="flex items-center justify-center gap-3 mt-6">
              <img src={kodyMascot} alt="Kody" className="w-8 h-8 object-contain" />
              <div className="text-left">
                <p className="text-xs font-bold text-foreground">CodeMasterIP</p>
                <p className="text-[10px] text-muted-foreground">Creator of Kody & CryptoKody</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center"
        >
          <a
            href="https://codemasterip.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 transition-colors shadow-[0_0_30px_-5px_hsl(168_80%_48%/0.4)]"
          >
            <Award className="w-4 h-4" />
            {c.cta}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default CodeMasterShowcase;
